import {
    memo,
    useState,
    ReactElement,
    useMemo,
    useCallback,
    useRef,
    useEffect
} from "react";

import { HttpService } from "../../../../services/HttpService";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import RequestStepper from "./Stepper";
import DocumentCard from "./AvailabeDocuments";
import { Order, RequestFormRef, RequestFormType, TermsOfRequestType } from "../types";
import RequestForm from "./RequestForm";
import OrderSummary from "./OrderSummary";
import Payment from "./Payment";
import Terms from "./Terms";
import { useToast } from "../../../../hooks/useToast";


type PropTypes = {
    orders: Order[]
    visible: boolean,
    onCloseHandler: () => void
};


function RequestShippmentDialog(props: PropTypes): ReactElement {

    const {
        orders,
        visible,
        onCloseHandler
    } = props;

    const { show } = useToast();
    const formRef = useRef<RequestFormRef>(null);

    const [step, setStep] = useState<number>(1);


    const [fileuuId, setfileuuId] = useState<string>('');
    const [showPayment, setShowPayment] = useState(false);
    const [termsOfRequest, setTermsOfRequest] = useState<TermsOfRequestType>({
        accept: false,
        acknowledge: false
    })

    const [formData, setFormData] = useState<RequestFormType>({
        fullName: '',
        deliveryAddress: {
            city: '',
            state: '',
            countryCode: 'US',
            zipCode: '',
            addressLine1: '',
            addressLine2: '',
            phoneNumber: '',
            addressType: 'RESIDENTIAL',
        },
    });

    const handleSubmit = useCallback((formData: any) => {
        setFormData(formData);
        const filingIds = orders.map((order: Order) => order.filingId);
       // formData.deliveryAddress.countryCode = "US"
        HttpService.post("/api/return-shipment", {
            ...formData,
            filingIds,
        }).then(res => {
            setStep(3);
            setfileuuId(res.data.uuid);
        }).catch(err => {
           show({
            detail:"Something went wrong while requesting physical copies.",
            severity: "error",
           })
        });

    }, [orders]);

    const handleRequest = useCallback(() => {
        setShowPayment(true);
    }, [orders]);

    const handleBack = useCallback(() => {
        setStep(prev => {
            if (prev > 1) {
                return --prev;
            }
            return prev
        })
    }, [setStep]);

    const handleClose = useCallback(() => {
        onCloseHandler();
        setStep(1)
    }, [setStep]);

    const handleNext = useCallback(async () => {

        if (formRef.current) {
            formRef.current.submit();
        } else {
            setStep(prev => {
                if (prev < 4) {
                    return ++prev;
                }
                return prev
            })
        }

    }, [setStep, formRef]);

    const { dialogTitle, dialogDescription } = useMemo(() => {

        let dialogTitle:string = ""
        let dialogDescription:string = "";

        switch (step) {
            case 1:     
                dialogTitle = "Review Available Physical Copies";
                dialogDescription = "Review the documents that will be included in your shipment.";
                break;
            case 2:
                dialogTitle = "Recipient Information";
                dialogDescription = "Enter the shipping address and contact information.";
                break;
            case 3:
                dialogTitle = "Terms for Requesting Physical Copies";
                dialogDescription = '';
                break;
            case 4:
                dialogTitle = "Review and Pay";
                dialogDescription = "";
                break;
        }
        
        return {
            dialogTitle,
            dialogDescription
        }
    }, [step]);



    return (
        <Dialog visible={visible}
            style={{ width: '50rem' }}
            onHide={handleClose}
            header={
                <div className="flex flex-column gap-2">
                    <h1 className="text-xl font-bold">{dialogTitle}</h1>
                    <div className="text-color text-base font-normal">{dialogDescription}</div>
                </div>
            }
            footer={
                <div className="flex justify-content-between align-items-center pt-3">
                    <Button outlined
                        severity="secondary"
                        {
                        ...(step > 1 ? {
                            label: "Back",
                            onClick: handleBack
                        } : {
                            label: "Cancel",
                            onClick: handleClose
                        })
                        }
                    />

                    <Button
                        {
                        ...(step < 4 ? (
                            {
                                label: "Continue",
                                onClick: handleNext
                            }
                        ) : ({
                            label: "Confirm & Pay",
                            onClick: handleRequest,
                            disabled: !termsOfRequest.accept || !termsOfRequest.acknowledge
                        }))
                        } />
                </div>
            }>
            <div className="w-full flex flex-column align-items-center gap-5">
                <RequestStepper step={step} />
                <div className="w-full">
                    {
                        step === 1 && <DocumentCard documents={orders} />
                    }
                    {
                        step === 2 && <RequestForm ref={formRef} onSubmit={handleSubmit} formData={formData} />
                    }
                    {
                        step === 3 && <Terms />
                    }
                    {
                        step === 4 && <OrderSummary formData={formData} orders={orders} termsOfRequest={termsOfRequest} setTermsOfRequest={setTermsOfRequest} />
                    }
                    {
                        (step === 4 && showPayment) ? <Payment uuid={fileuuId} handlePreviousStep={() => setShowPayment(false)} /> : null
                    }
                </div>
            </div>
        </Dialog>
    )
}

export default memo(RequestShippmentDialog);